import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, DollarSign, TrendingUp, Gift, ChevronDown, ChevronRight, Copy, Award, Download, Network } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import OrgChart from './OrgChart';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n);
const LEVEL_COLORS = ['bg-[#002FA7] text-white', 'bg-violet-500 text-white', 'bg-amber-500 text-white'];

// Flatten tree into a list with depth info
function flattenTree(nodes, depth) {
  const result = [];
  for (const node of nodes) {
    result.push({ ...node, depth });
    if (node.children && node.children.length > 0) {
      result.push(...flattenTree(node.children, depth + 1));
    }
  }
  return result;
}

export default function DownlineTab() {
  const [downline, setDownline] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [upline, setUpline] = useState([]);
  const [referralCode, setReferralCode] = useState('');
  const [subTab, setSubTab] = useState('network');
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [orgNodes, setOrgNodes] = useState([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const fetchData = useCallback(async () => {
    try {
      const [d, e, u, r, oc] = await Promise.all([
        axios.get(`${API}/downline`, { withCredentials: true }),
        axios.get(`${API}/downline/earnings`, { withCredentials: true }),
        axios.get(`${API}/downline/upline`, { withCredentials: true }),
        axios.get(`${API}/referral/code`, { withCredentials: true }),
        axios.get(`${API}/downline/org-chart`, { withCredentials: true }),
      ]);
      setDownline(d.data);
      setEarnings(e.data);
      setUpline(u.data);
      setReferralCode(r.data.code || '');
      setOrgNodes(oc.data.nodes || []);
      // Auto-expand first level
      if (d.data.tree) {
        setExpandedNodes(new Set(d.data.tree.map(n => n.user_id)));
      }
    } catch { /* fetch error */ }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const copyCode = () => { navigator.clipboard.writeText(referralCode); toast.success('Referral code copied!'); };
  const copyLink = () => { navigator.clipboard.writeText(`https://joinkrieger.com/apply?ref=${referralCode}`); toast.success('Referral link copied!'); };
  const toggleNode = (userId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await axios.get(`${API}/downline/override-report?month=${selectedMonth}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `krieger_override_report_${selectedMonth}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded!');
    } catch {
      toast.error('Failed to download report');
    }
    setDownloadingPdf(false);
  };

  // Flatten tree for rendering
  const flatNodes = downline?.tree ? flattenTree(downline.tree, 0) : [];
  // Filter to only show expanded nodes
  const visibleNodes = [];
  const hiddenParents = new Set();
  for (const node of flatNodes) {
    if (node.depth === 0) {
      visibleNodes.push(node);
    } else {
      // Check if all ancestors are expanded
      let parentVisible = true;
      // Simple check: if depth > 0, the parent at depth-1 must be expanded
      // Walk back to find the parent
      let parentIdx = visibleNodes.length - 1;
      while (parentIdx >= 0 && visibleNodes[parentIdx].depth >= node.depth) parentIdx--;
      if (parentIdx >= 0 && expandedNodes.has(visibleNodes[parentIdx].user_id)) {
        visibleNodes.push(node);
      }
    }
  }

  return (
    <div data-testid="downline-tab">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-900">My Network</h1>
          <p className="text-sm text-slate-400">Revenue share from your downline. Recruit agents, earn overrides on their deals.</p>
        </div>
      </div>

      {referralCode && (
        <div className="bg-[#002FA7] text-white rounded-xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="referral-code-banner">
          <div className="flex-1">
            <div className="text-xs opacity-70 mb-0.5">Your Referral Code</div>
            <div className="text-lg font-['Cabinet_Grotesk'] font-bold tracking-wider">{referralCode}</div>
          </div>
          <div className="flex gap-2">
            <Button onClick={copyCode} variant="secondary" size="sm" className="text-[10px] h-7 gap-1 bg-white/10 hover:bg-white/20 text-white border-0"><Copy size={10} /> Code</Button>
            <Button onClick={copyLink} variant="secondary" size="sm" className="text-[10px] h-7 gap-1 bg-white/10 hover:bg-white/20 text-white border-0"><Copy size={10} /> Link</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-slate-200 p-4 rounded-xl">
          <div className="text-[10px] tracking-wider uppercase text-slate-400 font-semibold">Network Size</div>
          <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-900">{downline?.total_network || 0}</div>
          <div className="text-xs text-slate-400">{downline?.total_directs || 0} direct recruits</div>
        </div>
        <div className="bg-white border border-slate-200 p-4 rounded-xl">
          <div className="text-[10px] tracking-wider uppercase text-slate-400 font-semibold">Total Earned</div>
          <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-emerald-600">{fmt(earnings?.total_earned || 0)}</div>
          <div className="text-xs text-slate-400">deal overrides + bonuses</div>
        </div>
        <div className="bg-white border border-slate-200 p-4 rounded-xl">
          <div className="text-[10px] tracking-wider uppercase text-slate-400 font-semibold">Deal Overrides</div>
          <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-[#002FA7]">{fmt(earnings?.total_deal_overrides || 0)}</div>
          <div className="text-xs text-slate-400">from downline deals</div>
        </div>
        <div className="bg-white border border-slate-200 p-4 rounded-xl">
          <div className="text-[10px] tracking-wider uppercase text-slate-400 font-semibold">Recruit Bonuses</div>
          <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-amber-600">{fmt(earnings?.total_recruitment_bonuses || 0)}</div>
          <div className="text-xs text-slate-400">${earnings?.recruitment_bonus || 250} per approved recruit</div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-[#0F1729] to-[#002FA7] text-white rounded-xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-3"><Award size={16} className="text-amber-300" /><span className="text-sm font-semibold">Your Override Rates</span></div>
        <div className="grid grid-cols-3 gap-4">
          {[['1', 5, 'Direct'], ['2', 3, '2nd Gen'], ['3', 1, '3rd Gen']].map(([lvl, rate, label]) => (
            <div key={lvl} className="text-center">
              <div className="text-2xl font-['Cabinet_Grotesk'] font-bold">{(earnings?.override_rates || {})[lvl] || rate}%</div>
              <div className="text-[10px] opacity-60">Level {lvl} ({label})</div>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-white/10 text-[10px] opacity-60">
          + ${earnings?.recruitment_bonus || 250} one-time bonus per approved direct recruit · No cap on total override earnings
        </div>
      </div>

      <div className="flex gap-1 mb-4">
        {[{ id: 'network', label: 'Downline Tree' }, { id: 'org-chart', label: 'Org Chart' }, { id: 'earnings', label: 'Earnings History' }].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} className={`px-4 py-1.5 rounded-lg text-xs font-medium ${subTab === t.id ? 'bg-[#002FA7] text-white' : 'text-slate-500 bg-slate-100'}`} data-testid={`subtab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {subTab === 'network' && (
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="downline-tree">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Your Downline (3 Levels Deep)</h3>
          {upline.length > 0 && (
            <div className="mb-4 p-3 bg-slate-50 rounded-lg">
              <div className="text-[10px] text-slate-400 font-semibold mb-1">Your Upline</div>
              {upline.map(u => (
                <div key={u.user_id} className="text-xs text-slate-600">{u.name} (Level {u.level} above you)</div>
              ))}
            </div>
          )}
          {visibleNodes.length === 0 ? (
            <div className="text-center py-10">
              <Users size={40} className="text-slate-200 mx-auto mb-3" />
              <p className="text-sm text-slate-400 mb-2">No recruits in your downline yet.</p>
              <p className="text-xs text-slate-400">Share your referral code to start building your network and earning overrides.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {visibleNodes.map(node => {
                const hasKids = node.children && node.children.length > 0;
                const isExpanded = expandedNodes.has(node.user_id);
                return (
                  <div key={node.user_id} style={{ marginLeft: `${node.depth * 28}px` }} className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-slate-50">
                    {hasKids ? (
                      <button onClick={() => toggleNode(node.user_id)} className="w-5 h-5 rounded flex items-center justify-center bg-slate-100 hover:bg-slate-200 shrink-0">
                        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                    ) : <div className="w-5" />}
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${LEVEL_COLORS[node.depth] || 'bg-slate-200 text-slate-500'}`}>
                      {node.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-800 truncate">{node.name}</span>
                        <Badge className="text-[8px] bg-slate-50 text-slate-400 border-slate-200">L{node.level}</Badge>
                        {node.agent_status === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" title="Active" />}
                      </div>
                      <div className="text-[10px] text-slate-400">{node.deal_count} deals · {fmt(node.deal_volume)}{node.total_downstream > 0 ? ` · ${node.total_downstream} downstream` : ''}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {subTab === 'org-chart' && (
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="org-chart-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Network Org Chart</h3>
              <p className="text-[10px] text-slate-400 mt-0.5">Drag to pan, scroll to zoom. Click nodes to expand/collapse.</p>
            </div>
            <Badge className="text-[9px] bg-[#EFF3FF] text-[#002FA7] border-[#002FA7]/20">{orgNodes.length} member{orgNodes.length !== 1 ? 's' : ''}</Badge>
          </div>
          <OrgChart nodes={orgNodes} />
        </div>
      )}

      {subTab === 'earnings' && (
        <div className="space-y-4">
          {/* PDF Download */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="pdf-download-panel">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Override Payout Report</h3>
              <p className="text-[10px] text-slate-400">Download a PDF statement for accounting</p>
            </div>
            <div className="flex items-center gap-2">
              <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-[#002FA7]" data-testid="month-picker" />
              <Button onClick={downloadPdf} disabled={downloadingPdf} size="sm" className="text-xs gap-1.5 bg-[#002FA7] hover:bg-[#0040D6] h-8" data-testid="download-pdf-btn">
                <Download size={12} /> {downloadingPdf ? 'Generating...' : 'Download PDF'}
              </Button>
            </div>
          </div>

          {earnings?.monthly && Object.keys(earnings.monthly).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Monthly Override Earnings</h3>
              <div className="flex items-end gap-1.5 h-32">
                {Object.entries(earnings.monthly).map(([month, amt]) => {
                  const maxAmt = Math.max(...Object.values(earnings.monthly), 1);
                  const h = Math.max(8, (amt / maxAmt) * 110);
                  return (
                    <div key={month} className="flex flex-col items-center gap-0.5 min-w-[50px]">
                      <span className="text-[9px] text-slate-500">{fmt(amt)}</span>
                      <div className="w-7 rounded-t-md bg-emerald-500/80" style={{ height: `${h}px` }} />
                      <span className="text-[9px] text-slate-400">{month.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {earnings?.by_level && Object.keys(earnings.by_level).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Earnings by Level</h3>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(earnings.by_level).map(([lvl, data]) => (
                  <div key={lvl} className="bg-slate-50 rounded-lg p-3 text-center">
                    <div className="text-lg font-['Cabinet_Grotesk'] font-bold text-slate-800">{fmt(data.total)}</div>
                    <div className="text-[10px] text-slate-400">Level {lvl} ({data.rate}%) · {data.count} deals</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Recent Earnings</h3>
            {(!earnings?.recent || earnings.recent.length === 0) ? (
              <p className="text-sm text-slate-400 text-center py-6">No override earnings yet. Earnings appear when your downline closes deals.</p>
            ) : (
              <div className="space-y-1.5">
                {earnings.recent.map(e => (
                  <div key={e.id} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-100">
                    <div>
                      <div className="text-xs font-medium text-slate-800">{e.agent_name || 'Agent'}</div>
                      <div className="text-[10px] text-slate-400">{e.type === 'recruitment_bonus' ? 'Recruitment Bonus' : `L${e.level} Override (${e.override_rate}%)`} · {(e.created_at || '').slice(0, 10)}</div>
                    </div>
                    <div className="text-sm font-bold text-emerald-600">+{fmt(e.override_amount)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
